// client/src/components/common/StickySearchWrapper.js
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SearchBar from "./SearchBar";
// CSS loaded at page level - removed duplicate import
import { STICKY_SCROLL_THRESHOLD } from "../../constants/ui";

const StickySearchWrapper = ({
    searchTerm,
    onSearch,
    onReset,
    onFilterToggle,
    activeFilterCount = 0,
}) => {
    const { t } = useTranslation();
    const [isSticky, setIsSticky] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isScrollingUp, setIsScrollingUp] = useState(false);
    const lastScrollY = useRef(0);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const shouldBeSticky = scrollY > STICKY_SCROLL_THRESHOLD; // Trigger after configured threshold

            // Detect scroll direction
            const scrollingUp = scrollY < lastScrollY.current && scrollY > 100;
            const scrollingDown = scrollY > lastScrollY.current;

            if (shouldBeSticky !== isSticky) {
                setIsSticky(shouldBeSticky);
            }

            // Show collapsed button only when scrolling up
            if (shouldBeSticky) {
                if (scrollingUp && !isScrollingUp) {
                    setIsScrollingUp(true);
                    setIsExpanded(false);
                } else if (scrollingDown && isScrollingUp) {
                    setIsScrollingUp(false);
                    setIsExpanded(false);
                }
            } else {
                setIsScrollingUp(false);
                setIsExpanded(false);
            }

            lastScrollY.current = scrollY;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isSticky, isScrollingUp]);

    // Handle click outside to collapse
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        if (isExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded]);

    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div
            ref={wrapperRef}
            className={`sticky-search-wrapper ${isSticky && isScrollingUp ? "is-sticky" : ""} ${isExpanded ? "is-expanded" : ""}`}
        >
            <div className="sticky-search-container">
                {!isExpanded ? (
                    // Collapsed state - just a search button
                    <div className="sticky-search-collapsed">
                        <button
                            className="sticky-search-toggle-btn"
                            onClick={handleToggleExpand}
                            title="Open search"
                        >
                            <span className="search-icon" aria-hidden="true">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    width="1em"
                                    height="1em"
                                    focusable="false"
                                >
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                            </span>
                        </button>
                    </div>
                ) : (
                    // Expanded state - full search bar
                    <div className="sticky-search-content">
                        <SearchBar
                            searchTerm={searchTerm}
                            onSearch={onSearch}
                            onReset={onReset}
                            isSticky={isSticky}
                            placeholder="Search businesses..."
                        />

                        <button
                            className="filter-toggle-btn"
                            onClick={onFilterToggle}
                            title={t('filters.filtersTitle', { defaultValue: 'Filters' })}
                        >
                            <span className="filter-icon" aria-hidden="true">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    width="1em"
                                    height="1em"
                                    focusable="false"
                                >
                                    <polygon points="22 3 2 3 10 12 10 19 14 21 14 12 22 3" />
                                </svg>
                            </span>
                            <span className="filter-text">{t('filters.filtersTitle', { defaultValue: 'Filters' })}</span>
                            {activeFilterCount > 0 && (
                                <span className="filter-badge">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>

                        <button
                            className="sticky-search-close-btn"
                            onClick={handleToggleExpand}
                            title="Collapse search"
                        >
                            <span aria-hidden="true">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    width="1em"
                                    height="1em"
                                    focusable="false"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StickySearchWrapper;
